import React, { useState, useEffect } from "react";
import {
  getDatabase,
  ref,
  onValue,
  set,
  remove,
  push,
} from "firebase/database";
import firebaseApp from "../firebase";

const liveDb = getDatabase(firebaseApp);

const truncateText = (text, maxLength) => {
  if (!text) return "";
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + "...";
  }
  return text;
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({
    date: "",
    description: "",
    title: "",
  });
  const [editEvent, setEditEvent] = useState(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsRef = ref(liveDb, "events");
        onValue(eventsRef, (snapshot) => {
          const eventsData = snapshot.val();
          if (eventsData) {
            const eventsArray = Object.keys(eventsData)
              .map((key) => ({ id: key, ...eventsData[key] }))
              .sort((a, b) => new Date(b.date) - new Date(a.date));
            setEvents(eventsArray);
          }
        });
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };

    fetchEvents();
  }, []);

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    if (!newEvent.date || !newEvent.title || !newEvent.description) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      const eventsRef = ref(liveDb, "events");
      const newEventRef = push(eventsRef);
      await set(newEventRef, newEvent);
      alert("Event added successfully.");
      setNewEvent({ date: "", description: "", title: "" });
    } catch (error) {
      console.error("Error saving event:", error);
      alert("Failed to save event.");
    }
  };

  const handleEditEvent = (event) => {
    setEditEvent(event);
    setIsEventModalOpen(true);
  };

  const handleEditEventSubmit = async (e) => {
    e.preventDefault();
    if (!editEvent.date || !editEvent.title || !editEvent.description) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      const eventRef = ref(liveDb, `events/${editEvent.id}`);
      await set(eventRef, {
        date: editEvent.date,
        title: editEvent.title,
        description: editEvent.description,
      });
      alert("Event updated successfully.");
      setIsEventModalOpen(false);
      setEditEvent(null);
    } catch (error) {
      console.error("Error updating event:", error);
      alert("Failed to update event.");
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      const eventRef = ref(liveDb, `events/${id}`);
      await remove(eventRef);
      alert("Event deleted successfully.");
    } catch (error) {
      console.error("Error deleting event:", error);
      alert("Failed to delete event.");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg mt-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
        Event Management
      </h1>
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">Add Event</h2>
        <form onSubmit={handleEventSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-center">
            <label className="text-sm font-medium text-gray-700">Date</label>
            <input
              type="text"
              placeholder="YYYY-MM-DD"
              value={newEvent.date}
              onChange={(e) =>
                setNewEvent({ ...newEvent, date: e.target.value })
              }
              className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-center">
            <label className="text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              placeholder="Event title"
              value={newEvent.title}
              onChange={(e) =>
                setNewEvent({ ...newEvent, title: e.target.value })
              }
              className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-start">
            <label className="text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              placeholder="Event description"
              value={newEvent.description}
              onChange={(e) =>
                setNewEvent({ ...newEvent, description: e.target.value })
              }
              className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
              rows="4"
              required
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition duration-200"
            >
              Add Event
            </button>
          </div>
        </form>
      </section>
      <h3 className="text-lg font-semibold text-gray-700 mb-4">
        Existing Events
      </h3>
      {events.length === 0 ? (
        <p className="text-gray-600">No events found.</p>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <div
              key={event.id}
              className="border border-gray-200 rounded-md p-4 bg-gray-50 hover:bg-gray-100 transition duration-200"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    <strong>Date:</strong> {event.date}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Title:</strong> {truncateText(event.title, 50)}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Description:</strong>{" "}
                    {truncateText(event.description, 100)}
                  </p>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => handleEditEvent(event)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-200"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteEvent(event.id)}
                    className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition duration-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {isEventModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">
              Edit Event
            </h2>
            <form onSubmit={handleEditEventSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-center">
                <label className="text-sm font-medium text-gray-700">
                  Date
                </label>
                <input
                  type="text"
                  value={editEvent?.date || ""}
                  onChange={(e) =>
                    setEditEvent({ ...editEvent, date: e.target.value })
                  }
                  className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-center">
                <label className="text-sm font-medium text-gray-700">
                  Title
                </label>
                <input
                  type="text"
                  value={editEvent?.title || ""}
                  onChange={(e) =>
                    setEditEvent({ ...editEvent, title: e.target.value })
                  }
                  className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-start">
                <label className="text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  value={editEvent?.description || ""}
                  onChange={(e) =>
                    setEditEvent({ ...editEvent, description: e.target.value })
                  }
                  className="col-span-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  rows="4"
                  required
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition duration-200"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setIsEventModalOpen(false)}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
